import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Underline from "@tiptap/extension-underline"
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading2,
  Undo2,
  Redo2,
} from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Editor rich-text (Tiptap) para o "Modelo de contrato" / condições gerais.
 * Trabalha sobre o documento Tiptap JSON (ProseMirror doc). onChange devolve
 * o doc JSON pronto a guardar em settings.pdf_contract_model_json.
 */
export function TiptapEditor({
  value,
  onChange,
}: {
  value: unknown | null
  onChange: (doc: unknown) => void
}) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    // Só define o conteúdo inicial; o componente monta depois das settings
    // carregarem, por isso `value` já vem preenchido.
    content: isDoc(value) ? (value as object) : "",
    immediatelyRender: false,
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
    editorProps: {
      attributes: {
        class:
          "prose prose-sm max-w-none min-h-[12rem] rounded-b-md border border-t-0 border-input bg-background px-3 py-2 focus:outline-none",
      },
    },
  })

  if (!editor) return null

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1 rounded-t-md border border-input bg-muted/40 p-1">
        <ToolbarBtn editor={editor} label="Título" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn editor={editor} label="Negrito" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn editor={editor} label="Itálico" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn editor={editor} label="Sublinhado" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarBtn editor={editor} label="Lista" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn editor={editor} label="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" />
        </ToolbarBtn>
        <span className="mx-1 h-5 w-px bg-border" />
        <ToolbarBtn editor={editor} label="Anular" active={false} onClick={() => editor.chain().focus().undo().run()}>
          <Undo2 className="h-4 w-4" />
        </ToolbarBtn>
        <ToolbarBtn editor={editor} label="Refazer" active={false} onClick={() => editor.chain().focus().redo().run()}>
          <Redo2 className="h-4 w-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  )
}

function ToolbarBtn({
  active,
  onClick,
  label,
  children,
}: {
  editor: Editor
  active: boolean
  onClick: () => void
  label: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        "rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground",
        active && "bg-accent/15 text-accent",
      )}
    >
      {children}
    </button>
  )
}

/** true quando `v` parece um doc Tiptap (ProseMirror). */
function isDoc(v: unknown): boolean {
  return (
    !!v &&
    typeof v === "object" &&
    (v as { type?: string }).type === "doc" &&
    Array.isArray((v as { content?: unknown }).content)
  )
}
